import React from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { toast } from 'react-toastify';
import { ChevronLeft, FileText, MoreVertical, Smile, X } from 'lucide-react';
import data from '@emoji-mart/data';
import i18n from '@emoji-mart/data/i18n/vi.json';
import Picker from '@emoji-mart/react';

import AppAvatar from 'components/App/Avatar';
import AppButton from 'components/App/Button';

import INITIAL_VALUE from 'constants/initialValue';
import { LOCATIONS } from 'constants/index';

import imageIcon from 'assets/images/image-icon.svg';

import styles from './Chat.module.scss';

const ACCEPTED_FILE_TYPES = '.pdf,.png,.jpeg,.jpg';
const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024;
const IMAGE_FILE_REGEX = /\.(png|jpe?g)$/i;
const PDF_FILE_REGEX = /\.pdf$/i;
const MESSAGE_URL_REGEX = /https?:\/\/[^\s<>"']+/gi;
const MESSAGE_DATA_LINK_REGEX = /\[link\s+data-(booking|place)-id=["']([^"']+)["']\](.*?)\[\/link\]/gi;
const LOAD_MORE_MESSAGES_THRESHOLD = 80;

const SCROLL_BOTTOM_THRESHOLD = 80;

const scrollMessageAreaToBottom = (messageArea) => {
  if (!messageArea) {
    return;
  }

  const area = messageArea;

  const scroll = () => {
    area.scrollTop = area.scrollHeight;
  };

  scroll();
  requestAnimationFrame(() => {
    scroll();
    requestAnimationFrame(scroll);
  });
};

const getDistanceFromBottom = (messageArea) => (
  messageArea.scrollHeight - messageArea.scrollTop - messageArea.clientHeight
);

const getCustomerLevelLabel = (level) => {
  switch (level) {
    case 'MEMBER':
      return 'THÀNH VIÊN';
    case 'TOURIST':
      return 'DU KHÁCH';
    case 'ENTERPRISE':
      return 'DOANH NGHIỆP';
    case 'AGENT':
      return 'ĐẠI LÝ';
    default:
      return 'THÀNH VIÊN';
  }
};

const getAllowedLinkOrigin = () => window.location.origin;

const extractLinksFromMessage = (text) => text.match(MESSAGE_URL_REGEX) || [];

const validateMessageLinks = (text) => {
  const links = extractLinksFromMessage(text);

  if (links.length === 0) {
    return null;
  }

  const allowedOrigin = getAllowedLinkOrigin();

  const invalidLink = links.find((link) => {
    const cleanLink = link.replace(/[.,;:!?)]+$/, '');

    try {
      const { origin } = new URL(cleanLink);
      return origin !== allowedOrigin;
    } catch {
      return true;
    }
  });

  if (!invalidLink) {
    return null;
  }

  return 'Link không hợp lệ.';
};

const cleanLinkUrl = (url) => {
  const trailingPunctuationMatch = url.match(/[.,;:!?)]+$/);

  if (!trailingPunctuationMatch) {
    return { href: url, suffix: '' };
  }

  return {
    href: url.slice(0, -trailingPunctuationMatch[0].length),
    suffix: trailingPunctuationMatch[0],
  };
};

const resolveDataLinkHref = (type, id) => {
  if (type === 'booking') {
    return `${LOCATIONS.MANAGER_BOOKING}/${id}`;
  }

  return `${LOCATIONS.LOCATION_DETAIL.DEFAULT}?place=${id}`;
};

const renderPlainTextWithHttpLinks = (text, keyPrefix) => {
  const segments = text.split(/(https?:\/\/[^\s<>"']+)/i);
  let offset = 0;

  return segments.map((segment) => {
    const segmentKey = `${keyPrefix}-${offset}-${segment}`;
    offset += segment.length;

    if (!segment) {
      return null;
    }

    if (!/^https?:\/\//i.test(segment)) {
      return <React.Fragment key={segmentKey}>{segment}</React.Fragment>;
    }

    const { href, suffix } = cleanLinkUrl(segment);

    return (
      <React.Fragment key={segmentKey}>
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className={styles.MessageLink}
        >
          {href}
        </a>
        {suffix}
      </React.Fragment>
    );
  });
};

const renderMessageContent = (text) => {
  if (!text) {
    return null;
  }

  const parts = [];
  let lastIndex = 0;
  let partIndex = 0;
  const regex = new RegExp(MESSAGE_DATA_LINK_REGEX.source, 'gi');
  let match = regex.exec(text);

  while (match) {
    const [fullMatch, type, id, label] = match;

    if (match.index > lastIndex) {
      parts.push(
        ...renderPlainTextWithHttpLinks(
          text.slice(lastIndex, match.index),
          `plain-${partIndex}`,
        ),
      );
      partIndex += 1;
    }

    parts.push(
      <Link
        key={`data-link-${partIndex}-${type}-${id}`}
        to={resolveDataLinkHref(type, id)}
        className={styles.MessageDataLink}
      >
        {label}
      </Link>,
    );
    partIndex += 1;
    lastIndex = match.index + fullMatch.length;
    match = regex.exec(text);
  }

  if (lastIndex < text.length) {
    parts.push(
      ...renderPlainTextWithHttpLinks(
        text.slice(lastIndex),
        `plain-${partIndex}`,
      ),
    );
  }

  return parts;
};

const getFileKey = (file) => `${file.name}-${file.size}-${file.lastModified}`;

const getCurrentTimeLabel = () => {
  const now = new Date();

  return `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
};

const getMessageDateKey = (createdAt) => {
  if (!createdAt) {
    return '';
  }

  const date = new Date(createdAt);

  if (Number.isNaN(date.getTime())) {
    return '';
  }

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
};

const formatMessageDateLabel = (createdAt) => {
  if (!createdAt) {
    return '';
  }

  const date = new Date(createdAt);

  if (Number.isNaN(date.getTime())) {
    return '';
  }

  const today = new Date();
  const isToday = (
    date.getFullYear() === today.getFullYear()
    && date.getMonth() === today.getMonth()
    && date.getDate() === today.getDate()
  );

  if (isToday) {
    return 'Hôm nay';
  }

  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();

  return `${day}/${month}/${year}`;
};

const formatFileSize = (bytes) => {
  if (bytes < 1024) {
    return `${bytes} B`;
  }

  if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toFixed(2)} KB`;
  }

  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

const getFileTypeLabel = (file) => {
  const extension = file.name.split('.').pop();

  if (extension) {
    return extension.toUpperCase();
  }

  if (file.type.includes('pdf') || PDF_FILE_REGEX.test(file.name)) {
    return 'PDF';
  }

  return 'FILE';
};

const isImageFile = (file) => ['image/png', 'image/jpeg'].includes(file.type) || IMAGE_FILE_REGEX.test(file.name);

const isPdfFile = (file) => file.type === 'application/pdf' || PDF_FILE_REGEX.test(file.name);

const isAllowedFile = (file) => isImageFile(file) || isPdfFile(file);

const isValidFileSize = (file) => file.size <= MAX_FILE_SIZE_BYTES;

const validateSelectedFile = (file) => {
  if (!isAllowedFile(file)) {
    return `File "${file.name}" không được hỗ trợ. Chỉ được phép tải lên file ảnh (PNG, JPEG, JPG) hoặc PDF.`;
  }

  if (!isValidFileSize(file)) {
    return `File "${file.name}" vượt quá dung lượng cho phép (10 MB).`;
  }

  return null;
};

// const downloadAttachment = (attachment) => {
//   if (!attachment?.url) {
//     return;
//   }

//   const link = document.createElement('a');
//   link.href = attachment.url;
//   link.download = attachment.name;
//   link.rel = 'noopener noreferrer';
//   document.body.appendChild(link);
//   link.click();
//   document.body.removeChild(link);
// };

const openAttachmentInNewTab = (attachment) => {
  if (!attachment?.url) {
    return;
  }

  window.open(attachment.url, '_blank', 'noopener,noreferrer');
};

function Chat(props) {
  const {
    bookingInfo,
    selectedId,
    messages,
    isLoadingMessages,
    hasMoreMessages,
    onLoadMoreMessages,
    isVisible,
    isSendingMessage,
    sentClientIds,
    onSendMessage,
    onBack,
    showBookingInfoToggle,
    onOpenBookingInfo,
  } = props;

  const customerName = bookingInfo?.user?.fullName || '';
  const customerRank = getCustomerLevelLabel(bookingInfo?.user?.level);
  const conversationId = selectedId || '';

  const [message, setMessage] = React.useState('');
  const [isEmojiPickerOpen, setIsEmojiPickerOpen] = React.useState(false);
  const [selectedFiles, setSelectedFiles] = React.useState([]);
  const [previewImage, setPreviewImage] = React.useState(null);
  const [pendingMessages, setPendingMessages] = React.useState([]);
  const [emojiPickerPosition, setEmojiPickerPosition] = React.useState({
    top: 0,
    left: 0,
  });
  const [composerKey, setComposerKey] = React.useState(0);

  const messageInputRef = React.useRef(null);
  const messageAreaRef = React.useRef(null);
  const messageListInnerRef = React.useRef(null);
  const emojiToggleRef = React.useRef(null);
  const emojiPickerPortalRef = React.useRef(null);
  const fileInputRef = React.useRef(null);
  const prevConversationIdRef = React.useRef(conversationId);
  const prevFirstMessageIdRef = React.useRef(null);
  const prevMessagesLengthRef = React.useRef(0);
  const prevScrollHeightRef = React.useRef(0);
  const pendingLoadMoreScrollRef = React.useRef(null);
  const stickToBottomRef = React.useRef(true);
  const prevVisibleRef = React.useRef(isVisible);
  const prevIsSendingRef = React.useRef(isSendingMessage);
  const isSubmittingRef = React.useRef(false);
  const messageRef = React.useRef('');
  const isComposingRef = React.useRef(false);
  const pendingSendRef = React.useRef(false);

  const orderedMessages = React.useMemo(() => [...messages].reverse(), [messages]);

  const displayMessages = React.useMemo(
    () => {
      const visiblePending = pendingMessages.filter(
        (item) => !sentClientIds.includes(item.clientId),
      );

      return [...orderedMessages, ...visiblePending];
    },
    [orderedMessages, pendingMessages, sentClientIds],
  );

  React.useEffect(() => {
    if (sentClientIds.length === 0) {
      return;
    }

    setPendingMessages(
      (prev) => prev.filter((item) => !sentClientIds.includes(item.clientId)),
    );
  }, [sentClientIds]);

  React.useEffect(() => {
    if (prevIsSendingRef.current && !isSendingMessage) {
      setPendingMessages([]);
    }

    prevIsSendingRef.current = isSendingMessage;
  }, [isSendingMessage]);

  React.useEffect(() => {
    if (composerKey > 0) {
      messageInputRef.current?.focus();
    }
  }, [composerKey]);

  React.useEffect(() => {
    messageRef.current = message;
  }, [message]);

  React.useEffect(() => {
    setPendingMessages([]);
    isSubmittingRef.current = false;
    pendingLoadMoreScrollRef.current = null;
    stickToBottomRef.current = true;
    prevMessagesLengthRef.current = 0;
    prevFirstMessageIdRef.current = null;
    prevScrollHeightRef.current = 0;
  }, [conversationId]);

  React.useLayoutEffect(() => {
    if (pendingMessages.length > 0 && messageAreaRef.current) {
      stickToBottomRef.current = true;
      scrollMessageAreaToBottom(messageAreaRef.current);
    }
  }, [pendingMessages]);

  React.useLayoutEffect(() => {
    const messageArea = messageAreaRef.current;

    if (!messageArea || !isVisible || messages.length === 0) {
      if (prevConversationIdRef.current !== conversationId) {
        prevConversationIdRef.current = conversationId;
        prevMessagesLengthRef.current = 0;
        prevFirstMessageIdRef.current = null;
        prevScrollHeightRef.current = 0;
        stickToBottomRef.current = true;
        pendingLoadMoreScrollRef.current = null;
      }

      prevVisibleRef.current = isVisible;
      return;
    }

    const isConversationChanged = prevConversationIdRef.current !== conversationId;
    const isPaneJustVisible = isVisible && !prevVisibleRef.current;
    const firstMessageId = messages[0]?.id ?? null;

    const isLoadMoreOlder = prevMessagesLengthRef.current > 0
      && firstMessageId !== null
      && firstMessageId === prevFirstMessageIdRef.current
      && messages.length > prevMessagesLengthRef.current;

    const isInitialMessagesLoaded = prevMessagesLengthRef.current === 0 && messages.length > 0;

    const isNewNewestMessage = prevMessagesLengthRef.current > 0
      && firstMessageId !== null
      && firstMessageId !== prevFirstMessageIdRef.current
      && messages.length > prevMessagesLengthRef.current;

    if (isLoadMoreOlder) {
      stickToBottomRef.current = false;

      const snapshot = pendingLoadMoreScrollRef.current;
      const previousScrollHeight = snapshot?.scrollHeight ?? prevScrollHeightRef.current;
      const previousScrollTop = snapshot?.scrollTop ?? messageArea.scrollTop;

      messageArea.scrollTop = previousScrollTop + (messageArea.scrollHeight - previousScrollHeight);
      pendingLoadMoreScrollRef.current = null;
    } else if (
      isConversationChanged
      || isPaneJustVisible
      || isInitialMessagesLoaded
      || isNewNewestMessage
    ) {
      pendingLoadMoreScrollRef.current = null;
      stickToBottomRef.current = true;
      scrollMessageAreaToBottom(messageArea);
    } else if (stickToBottomRef.current) {
      scrollMessageAreaToBottom(messageArea);
    }

    prevScrollHeightRef.current = messageArea.scrollHeight;
    prevConversationIdRef.current = conversationId;
    prevFirstMessageIdRef.current = firstMessageId;
    prevMessagesLengthRef.current = messages.length;
    prevVisibleRef.current = isVisible;
  }, [messages, conversationId, isVisible]);

  React.useEffect(() => {
    const messageArea = messageAreaRef.current;
    const messageListInner = messageListInnerRef.current;

    if (!messageArea || !messageListInner || !isVisible) {
      return undefined;
    }

    const keepAtBottomIfNeeded = () => {
      if (stickToBottomRef.current) {
        scrollMessageAreaToBottom(messageArea);
      }
    };

    const resizeObserver = typeof ResizeObserver !== 'undefined'
      ? new ResizeObserver(keepAtBottomIfNeeded)
      : null;

    resizeObserver?.observe(messageListInner);

    return () => {
      resizeObserver?.disconnect();
    };
  }, [conversationId, isVisible, displayMessages.length]);

  const handleMessageAreaScroll = React.useCallback((event) => {
    const messageArea = event.currentTarget;
    const distanceFromBottom = getDistanceFromBottom(messageArea);

    if (distanceFromBottom <= SCROLL_BOTTOM_THRESHOLD) {
      stickToBottomRef.current = true;
    } else if (messageArea.scrollTop > LOAD_MORE_MESSAGES_THRESHOLD) {
      stickToBottomRef.current = false;
    }

    if (!hasMoreMessages || isLoadingMessages) {
      return;
    }

    if (messageArea.scrollTop > LOAD_MORE_MESSAGES_THRESHOLD) {
      return;
    }

    stickToBottomRef.current = false;
    pendingLoadMoreScrollRef.current = {
      scrollTop: messageArea.scrollTop,
      scrollHeight: messageArea.scrollHeight,
    };
    prevScrollHeightRef.current = messageArea.scrollHeight;
    onLoadMoreMessages();
  }, [hasMoreMessages, isLoadingMessages, onLoadMoreMessages]);

  React.useEffect(() => {
    if (!isEmojiPickerOpen) {
      return undefined;
    }

    const updateEmojiPickerPosition = () => {
      if (!emojiToggleRef.current) {
        return;
      }

      const toggleRect = emojiToggleRef.current.getBoundingClientRect();

      setEmojiPickerPosition({
        top: toggleRect.top - 8,
        left: toggleRect.right,
      });
    };

    const handleClickOutside = (event) => {
      const isClickOnToggle = emojiToggleRef.current
        && emojiToggleRef.current.contains(event.target);

      const isClickOnPicker = emojiPickerPortalRef.current
        && emojiPickerPortalRef.current.contains(event.target);

      if (!isClickOnToggle && !isClickOnPicker) {
        setIsEmojiPickerOpen(false);
      }
    };

    updateEmojiPickerPosition();
    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('resize', updateEmojiPickerPosition);
    window.addEventListener('scroll', updateEmojiPickerPosition, true);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('resize', updateEmojiPickerPosition);
      window.removeEventListener('scroll', updateEmojiPickerPosition, true);
    };
  }, [isEmojiPickerOpen]);

  React.useEffect(() => {
    setPreviewImage(null);
  }, [customerName]);

  React.useEffect(() => {
    if (!previewImage) {
      return undefined;
    }

    const handleEscapeKey = (event) => {
      if (event.key === 'Escape') {
        setPreviewImage(null);
      }
    };

    document.addEventListener('keydown', handleEscapeKey);

    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [previewImage]);

  const previewFiles = React.useMemo(
    () => selectedFiles.map((file) => ({
      file,
      key: getFileKey(file),
      isImage: isImageFile(file),
      previewUrl: isImageFile(file) ? URL.createObjectURL(file) : '',
      typeLabel: getFileTypeLabel(file),
      sizeLabel: formatFileSize(file.size),
    })),
    [selectedFiles],
  );

  React.useEffect(
    () => () => {
      previewFiles.forEach((item) => {
        if (item.previewUrl) {
          URL.revokeObjectURL(item.previewUrl);
        }
      });
    },
    [previewFiles],
  );

  const handleEmojiSelect = (emoji) => {
    setMessage((prev) => `${prev}${emoji.native}`);
    setIsEmojiPickerOpen(false);
  };

  const handleToggleEmojiPicker = () => {
    setIsEmojiPickerOpen((prev) => !prev);
  };

  const handleSend = () => {
    if (isSubmittingRef.current) {
      return;
    }

    const trimmedMessage = messageRef.current.trim();

    if (!trimmedMessage && selectedFiles.length === 0) {
      return;
    }

    const linkError = validateMessageLinks(trimmedMessage);

    if (linkError) {
      toast.error(linkError);
      return;
    }

    const timeLabel = getCurrentTimeLabel();
    const createdAt = new Date().toISOString();
    const pendingBaseId = `pending-${Date.now()}`;
    const textClientId = trimmedMessage ? `${pendingBaseId}-text` : null;

    const files = selectedFiles.map((file, index) => ({
      file,
      isImage: isImageFile(file),
      name: file.name,
      size: formatFileSize(file.size),
      type: getFileTypeLabel(file),
      clientId: `${pendingBaseId}-file-${index}`,
    }));

    const optimisticMessages = [];

    if (trimmedMessage) {
      optimisticMessages.push({
        id: textClientId,
        clientId: textClientId,
        type: 'outgoing',
        content: trimmedMessage,
        attachments: [],
        time: timeLabel,
        createdAt,
        isPending: true,
      });
    }

    files.forEach((item) => {
      optimisticMessages.push({
        id: item.clientId,
        clientId: item.clientId,
        type: 'outgoing',
        content: '',
        attachments: [{
          id: `${item.clientId}-att`,
          isImage: item.isImage,
          url: item.isImage ? URL.createObjectURL(item.file) : '',
          name: item.name,
          sizeLabel: item.size,
          typeLabel: item.type,
        }],
        time: timeLabel,
        createdAt,
        isPending: true,
      });
    });

    isSubmittingRef.current = true;

    setPendingMessages((prev) => [...prev, ...optimisticMessages]);

    onSendMessage({
      text: trimmedMessage,
      textClientId,
      files,
    });
    messageRef.current = '';
    setMessage('');
    setSelectedFiles([]);
    setComposerKey((prev) => prev + 1);

    setTimeout(() => {
      isSubmittingRef.current = false;
    }, 0);
  };

  const handleOpenFilePicker = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = (event) => {
    const files = Array.from(event.target.files || []);
    const validFiles = [];

    files.forEach((file) => {
      const errorMessage = validateSelectedFile(file);

      if (errorMessage) {
        toast.error(errorMessage);
        return;
      }

      validFiles.push(file);
    });

    if (validFiles.length > 0) {
      setSelectedFiles((prev) => {
        const existingKeys = new Set(prev.map((file) => getFileKey(file)));
        const newFiles = validFiles.filter((file) => !existingKeys.has(getFileKey(file)));
        return [...prev, ...newFiles];
      });
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleRemoveFile = (fileKey) => {
    setSelectedFiles((prev) => prev.filter((file) => getFileKey(file) !== fileKey));
  };

  const handleOpenImagePreview = (attachment) => {
    setPreviewImage(attachment);
  };

  const handleCloseImagePreview = () => {
    setPreviewImage(null);
  };

  // const handleDownloadPreviewImage = () => {
  //   if (previewImage) {
  //     downloadAttachment(previewImage);
  //   }
  // };

  const handleCompositionStart = () => {
    isComposingRef.current = true;
  };

  const handleCompositionEnd = () => {
    isComposingRef.current = false;

    if (pendingSendRef.current) {
      pendingSendRef.current = false;
      setTimeout(handleSend, 0);
    }
  };

  const handleKeyDown = (event) => {
    if (event.key !== 'Enter' || event.shiftKey) {
      return;
    }

    if (isComposingRef.current || event.isComposing) {
      pendingSendRef.current = true;
      return;
    }

    event.preventDefault();
    handleSend();
  };

  if (!customerName) {
    return (
      <div className={styles.Chat}>
        <div className={styles.EmptyState}>
          Chọn một cuộc hội thoại để bắt đầu nhắn tin
        </div>
      </div>
    );
  }

  return (
    <div className={styles.Chat}>
      <div className={styles.Header}>
        {onBack && (
          <button
            type="button"
            className={styles.BackButton}
            aria-label="Quay lại danh sách tin nhắn"
            onClick={onBack}
          >
            <ChevronLeft size={24} />
          </button>
        )}
        <AppAvatar
          name={customerName}
          size={56}
          className={styles.HeaderAvatar}
        />
        <div className={styles.HeaderInfo}>
          <p className={styles.CustomerName}>{customerName}</p>
          <span className={styles.CustomerRank}>{customerRank}</span>
        </div>
        {showBookingInfoToggle && (
          <button
            type="button"
            className={styles.MoreButton}
            aria-label="Xem thông tin đặt phòng"
            onClick={onOpenBookingInfo}
          >
            <MoreVertical size={24} />
          </button>
        )}
      </div>

      <div
        ref={messageAreaRef}
        className={styles.MessageArea}
        onScroll={handleMessageAreaScroll}
      >
        <div ref={messageListInnerRef} className={styles.MessageListInner}>
          <div className={styles.MessageListSpacer} aria-hidden="true" />
          {isLoadingMessages && messages.length === 0 ? (
            <div className={styles.EmptyState}>Đang tải tin nhắn...</div>
          ) : (
            <>
              <div className={styles.LoadingMoreSlot}>
                {isLoadingMessages && messages.length > 0 && (
                  <div className={styles.LoadingMoreMessages}>Đang tải tin nhắn cũ...</div>
                )}
              </div>

              {displayMessages.map((item, index) => {
                const dateKey = getMessageDateKey(item.createdAt);
                const previousDateKey = index > 0
                  ? getMessageDateKey(displayMessages[index - 1].createdAt)
                  : '';
                const showDateDivider = Boolean(dateKey) && dateKey !== previousDateKey;

                return (
                  <React.Fragment key={item.id}>
                    {showDateDivider && (
                      <div className={styles.DateDivider}>
                        <span>{formatMessageDateLabel(item.createdAt)}</span>
                      </div>
                    )}

                    <div
                      className={`${styles.MessageRow} ${item.type === 'outgoing' ? styles.Outgoing : styles.Incoming} ${item.isPending ? styles.Pending : ''}`}
                    >
                      {item.type === 'incoming' && (
                        <AppAvatar
                          name={customerName}
                          size={40}
                          className={styles.MessageAvatar}
                        />
                      )}
                      <div className={styles.MessageContent}>
                        {item.attachments?.length > 0 && (
                          <div className={item.type === 'outgoing' ? styles.MessageAttachmentList : styles.MessageAttachmentListIncoming}>
                            {item.attachments.map((attachment) => (
                              attachment.isImage && attachment.url ? (
                                <button
                                  key={attachment.id}
                                  type="button"
                                  className={styles.MessageImageAttachmentCard}
                                  aria-label={`Xem ảnh ${attachment.name}`}
                                  onClick={() => handleOpenImagePreview(attachment)}
                                >
                                  <img
                                    src={attachment.url}
                                    alt={attachment.name}
                                    className={styles.MessageImageAttachmentPreview}
                                  />
                                </button>
                              ) : (
                                <button
                                  key={attachment.id}
                                  type="button"
                                  className={styles.MessageAttachmentCard}
                                  disabled={!attachment.url}
                                  aria-label={`Mở ${attachment.name}`}
                                  onClick={() => openAttachmentInNewTab(attachment)}
                                >
                                  <div className={styles.AttachmentIcon}>
                                    <FileText size={20} />
                                  </div>
                                  <div className={styles.AttachmentInfo}>
                                    <p className={styles.AttachmentName} title={attachment.name}>
                                      {attachment.name}
                                    </p>
                                    <p className={styles.AttachmentMeta}>
                                      {attachment.sizeLabel}
                                      {' • '}
                                      {attachment.typeLabel}
                                    </p>
                                  </div>
                                </button>
                              )
                            ))}
                          </div>
                        )}
                        {item.content && (
                          <div className={styles.Bubble}>{renderMessageContent(item.content)}</div>
                        )}
                        <span className={styles.Time}>
                          {item.isPending ? 'Đang gửi...' : item.time}
                        </span>
                      </div>
                    </div>
                  </React.Fragment>
                );
              })}
            </>
          )}
        </div>
      </div>

      {
        bookingInfo?.isClosed ? (
          <div className={styles.ClosedChat}>Cuộc hội thoại đã đóng</div>
        ) :
          (
            <div className={styles.InputArea}>
              <div className={styles.InputAreaContent}>
                {previewFiles.length > 0 && (
                <div className={styles.AttachmentList}>
                  {previewFiles.map((item) => (
                    <div
                      key={item.key}
                      className={`${styles.AttachmentCard} ${item.isImage ? styles.ImageOnlyAttachmentCard : ''}`}
                    >
                      {item.isImage ? (
                        <img
                          src={item.previewUrl}
                          alt={item.file.name}
                          className={styles.ImageOnlyAttachmentPreview}
                        />
                      ) : (
                        <div className={styles.AttachmentIcon}>
                          <FileText size={20} />
                        </div>
                      )}
                      {!item.isImage && (
                      <div className={styles.AttachmentInfo}>
                        <p className={styles.AttachmentName}>
                          {item.file.name}
                        </p>
                        <p className={styles.AttachmentMeta}>
                          {item.sizeLabel}
                          {' • '}
                          {item.typeLabel}
                        </p>
                      </div>
                      )}
                      <button
                        type="button"
                        className={styles.AttachmentRemove}
                        aria-label="Bỏ tệp"
                        onClick={() => handleRemoveFile(item.key)}
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ))}
                </div>
                )}
                <textarea
                  key={composerKey}
                  ref={messageInputRef}
                  rows={3}
                  value={message}
                  className={styles.MessageInput}
                  placeholder="Nhập tin nhắn..."
                  onChange={(event) => setMessage(event.target.value)}
                  onKeyDown={handleKeyDown}
                  onCompositionStart={handleCompositionStart}
                  onCompositionEnd={handleCompositionEnd}
                />
                <div className={styles.InputToolbar}>
                  <div className={styles.InputActions}>
                    <input
                      ref={fileInputRef}
                      type="file"
                      multiple
                      className={styles.HiddenFileInput}
                      accept={ACCEPTED_FILE_TYPES}
                      onChange={handleFileChange}
                    />
                    <button
                      type="button"
                      className={styles.IconButton}
                      aria-label="Tải file lên"
                      onClick={handleOpenFilePicker}
                    >
                      <img src={imageIcon} alt="" />
                    </button>
                  </div>
                  <div className={styles.InputToolbarRight}>
                    <button
                      ref={emojiToggleRef}
                      type="button"
                      className={styles.EmojiButton}
                      aria-label="Chọn emoji"
                      onClick={handleToggleEmojiPicker}
                    >
                      <Smile size={30} />
                    </button>
                    <AppButton
                      color="primary"
                      onClick={handleSend}
                      classes={styles.SendButton}
                    >
                      Gửi
                    </AppButton>
                  </div>
                </div>
              </div>
            </div>
          )
      }

      {isEmojiPickerOpen && createPortal(
        <div
          ref={emojiPickerPortalRef}
          className={styles.EmojiPickerPopover}
          style={{
            top: emojiPickerPosition.top,
            left: emojiPickerPosition.left,
            transform: 'translate(-100%, -100%)',
          }}
        >
          <Picker
            data={data}
            i18n={i18n}
            theme="light"
            previewPosition="none"
            skinTonePosition="none"
            onEmojiSelect={handleEmojiSelect}
          />
        </div>,
        document.body,
      )}
      {previewImage && createPortal(
        <div className={styles.ImagePreviewOverlay}>
          <button
            type="button"
            className={styles.ImagePreviewBackdrop}
            aria-label="Đóng"
            onClick={handleCloseImagePreview}
          />
          <div className={styles.ImagePreviewToolbar}>
            {/* <button
              type="button"
              className={styles.ImagePreviewAction}
              aria-label={`Tải xuống ${previewImage.name}`}
              onClick={handleDownloadPreviewImage}
            >
              <Download size={20} />
            </button> */}
            <button
              type="button"
              className={styles.ImagePreviewAction}
              aria-label="Đóng"
              onClick={handleCloseImagePreview}
            >
              <X size={20} />
            </button>
          </div>
          <div
            className={styles.ImagePreviewDialog}
            role="dialog"
            aria-modal="true"
            aria-label={`Xem ảnh ${previewImage.name}`}
          >
            <img
              src={previewImage.url}
              alt={previewImage.name}
              className={styles.ImagePreviewLarge}
            />
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
}

Chat.propTypes = {
  bookingInfo: PropTypes.instanceOf(Object),
  selectedId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  messages: PropTypes.instanceOf(Array),
  isLoadingMessages: PropTypes.bool,
  hasMoreMessages: PropTypes.bool,
  onLoadMoreMessages: PropTypes.func,
  isVisible: PropTypes.bool,
  isSendingMessage: PropTypes.bool,
  sentClientIds: PropTypes.instanceOf(Array),
  onSendMessage: PropTypes.func,
  onBack: PropTypes.func,
  showBookingInfoToggle: PropTypes.bool,
  onOpenBookingInfo: PropTypes.func,
};

Chat.defaultProps = {
  bookingInfo: INITIAL_VALUE.OBJECT,
  selectedId: null,
  messages: [],
  isLoadingMessages: false,
  hasMoreMessages: false,
  onLoadMoreMessages: INITIAL_VALUE.FUNCTION,
  isVisible: true,
  isSendingMessage: false,
  sentClientIds: [],
  onSendMessage: INITIAL_VALUE.FUNCTION,
  onBack: null,
  showBookingInfoToggle: false,
  onOpenBookingInfo: INITIAL_VALUE.FUNCTION,
};

export default Chat;
